import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import nexoraLogo from '../../assets/nexora-incon.png';

interface SecurityLayoutProps {
  variant: 'pending' | 'suspended' | 'blocked';
  icon: LucideIcon;
  title: string;
  description: string;
  buttonText?: string;
  buttonHref?: string;
  footerText?: string;
}

const variantStyles = {
  pending: {
    glow: 'bg-violet-500/12',
    border: 'border-violet-500/30',
    icon: 'text-violet-400',
    divider: 'bg-violet-500',
  },

  suspended: {
    glow: 'bg-amber-500/12',
    border: 'border-amber-500/30',
    icon: 'text-amber-400',
    divider: 'bg-amber-500',
  },

  blocked: {
    glow: 'bg-red-500/12',
    border: 'border-red-500/30',
    icon: 'text-red-400',
    divider: 'bg-red-500',
  },
};

export default function SecurityLayout({
  variant,
  icon: Icon,
  title,
  description,
  buttonText,
  buttonHref,
  footerText,
}: SecurityLayoutProps) {
  const styles = variantStyles[variant];

  const iconAnimation =
    variant === 'blocked'
      ? undefined
      : variant === 'suspended'
      ? {
          rotate: [-1, 1, -1],
        }
      : {
          scale: [1, 1.08, 1],
          opacity: [0.9, 1, 0.9],
        };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#020817] flex items-center justify-center px-6">

      {/* Glow esquerdo */}
      <div
        className={`absolute left-[-220px] top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[180px] ${styles.glow}`}
      />

      {/* Glow direito */}
      <div
        className={`absolute right-[-220px] top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[180px] ${styles.glow}`}
      />

      {/* Logo */}
      <div className="absolute top-9 left-9 opacity-60">
        <img
          src={nexoraLogo}
          alt="Nexora"
          className="w-10 h-10 object-contain"
        />
      </div>

      {/* Efeito Pending */}
      {variant === 'pending' && (
        <motion.div
          animate={{
            x: [-80, 80, -80],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'linear',
          }}
          className="
            absolute
            left-1/2
            top-[70px]
            h-[2px]
            w-[140px]
            -translate-x-1/2
            bg-gradient-to-r
            from-transparent
            via-violet-500
            to-transparent
            blur-sm
          "
        />
      )}

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`relative z-10 w-full text-center
        ${variant === 'suspended' ? 'max-w-xl' : 'max-w-md'}
       `}
      >


        {/* Ícone */}
        <motion.div
          animate={iconAnimation}
          transition={{
            duration: 2.5,
            repeat: Infinity,
          }}
          className={`
            mx-auto
            mb-6
            relative
            flex
            h-24
            w-24
            items-center
            justify-center
            rounded-3xl
            border
            bg-[#0f172a]/80
            backdrop-blur-xl
            shadow-[0_0_40px_rgba(255,255,255,0.03)]
            ${styles.border}
          `}
        >
          <>
  {variant === 'suspended' && (
    <>
      {/* Areia 1 */}
      <motion.div
        animate={{
          y: [-4, 24],
          opacity: [0, 1, 1, 0],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'linear',
        }}
        className="
          absolute
          left-1/2
          top-[34px]
          w-[2px]
          h-[2px]
          rounded-full
          bg-amber-300
          -translate-x-1/2
        "
      />

      {/* Areia 2 */}
      <motion.div
        animate={{
          y: [-4, 24],
          opacity: [0, 1, 1, 0],
        }}
        transition={{
          duration: 1.5,
          delay: 0.5,
          repeat: Infinity,
          ease: 'linear',
        }}
        className="
          absolute
          left-1/2
          top-[34px]
          w-[2px]
          h-[2px]
          rounded-full
          bg-amber-300
          -translate-x-1/2
        "
      />

      {/* Areia 3 */}
      <motion.div
        animate={{
          y: [-4, 24],
          opacity: [0, 1, 1, 0],
        }}
        transition={{
          duration: 1.5,
          delay: 1,
          repeat: Infinity,
          ease: 'linear',
        }}
        className="
          absolute
          left-1/2
          top-[34px]
          w-[2px]
          h-[2px]
          rounded-full
          bg-amber-300
          -translate-x-1/2
        "
      />

      {/* Brilho ao tocar o fundo */}
      <motion.div
        animate={{
          scale: [0, 1.5, 0],
          opacity: [0, 0.5, 0],
        }}
        transition={{
          duration: 1.5,
          delay: 1.35,
          repeat: Infinity,
        }}
        className="
          absolute
          left-1/2
          top-[60px]
          w-3
          h-3
          rounded-full
          bg-amber-300/30
          blur-md
          -translate-x-1/2
        "
      />
    </>
  )}

  <Icon className={`h-16 w-16 ${styles.icon}`} />
</>
        </motion.div>

        {/* Título */}
        <h1 className="text-5xl font-bold tracking-tight text-white whitespace-pre-line">
          {title}
        </h1>

        {/* Divider */}
        <div
          className={`w-20 h-1 mx-auto rounded-full mt-6 mb-6 ${styles.divider}`}
        />

        {/* Descrição */}
        <p className={` mx-auto text-[15px] leading-6 text-slate-300
        ${variant === 'suspended' ? 'max-w-[420px]' : 'max-w-[340px]'}
        `}>
          {description}
        </p>

        {/* CTA */}
        {buttonText && buttonHref && (
          <motion.a
            whileHover={{
              scale: 1.03,
            }}
            whileTap={{
              scale: 0.98,
            }}
            href={buttonHref}
            target="_blank"
            rel="noopener noreferrer"
            className="
              group
              relative
              inline-flex
              mt-7
              h-14
              items-center
              justify-center
              overflow-hidden
              rounded-2xl
              px-8
              font-semibold
              text-white
              bg-gradient-to-r
              from-violet-600
              to-purple-500
              shadow-[0_0_40px_rgba(139,92,246,0.35)]
              transition-all
              duration-300
              hover:scale-[1.03]
              hover:shadow-[0_0_60px_rgba(139,92,246,0.55)]
            "
          >
            <span className="relative z-10">
              {buttonText}
            </span>

            <span
              className="
                absolute
                inset-y-0
                left-[-100%]
                w-[100px]
                bg-gradient-to-r
                from-transparent
                via-white/20
                to-transparent
                transition-all
                duration-700
                group-hover:left-[120%]
              "
            />
          </motion.a>
        )}

        {/* Rodapé */}
        {footerText && (
          <p className="mt-9 text-xs text-slate-500">
            {footerText}
          </p>
        )}
      </motion.div>
    </div>
  );
}